import React, { useState } from "react";
import api from "../../api";
import { ScanLine, Search, Loader2, CheckCircle2, AlertTriangle, XCircle, User, BadgeCheck, Ban } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import QRScanner from "../../components/QRScanner";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso).slice(0, 16).replace("T", " "); }
};

export default function RedeemTab() {
  const [code, setCode] = useState("");
  const [scan, setScan] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {status:'ok'|'already'|'error', ...}

  const doCheckin = async (c) => {
    const val = (c || code).trim().toUpperCase();
    if (!val) return;
    setCode(val); setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/business/checkin", { code: val });
      setResult(data);
      if (data.status === "ok") toast({ title: "Checked in ✓", description: `${data.attendee_name} — ${data.tier_name}` });
      else if (data.status === "pass") toast({ title: `${data.pass_type} pass ✓`, description: `${data.holder_name} — access to all live events.` });
      else if (data.status === "revoked") toast({ title: "Pass revoked", description: `${data.holder_name}'s ${data.pass_type} pass is no longer valid.`, variant: "destructive" });
      else if (data.status === "expired") toast({ title: "Pass expired", description: `${data.holder_name}'s ${data.pass_type} pass has expired.`, variant: "destructive" });
      else if (data.status === "already") toast({ title: "Already checked in", description: `${data.attendee_name} was already admitted.`, variant: "destructive" });
    } catch (err) {
      setResult({ status: "error", detail: err?.response?.data?.detail || "Ticket not found" });
      toast({ title: "Check-in failed", description: err?.response?.data?.detail || "Ticket not found", variant: "destructive" });
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-[520px]">
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        <p className="text-[#5a6b80] text-sm mb-4">Scan a ticket QR at the door or enter the code to check the guest in.</p>
        <form onSubmit={(e) => { e.preventDefault(); doCheckin(); }} className="flex gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={10} placeholder="Enter ticket code" data-testid="checkin-code-input"
            className="flex-1 font-mono tracking-wider rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-[#2ec9c9] uppercase" />
          <button className="gp-btn-teal rounded-xl px-4 font-medium flex items-center gap-2" data-testid="checkin-lookup-btn"><Search size={18} /></button>
          <button type="button" onClick={() => setScan(true)} className="gp-btn-outline rounded-xl px-4 font-medium flex items-center gap-2" data-testid="checkin-scan-btn"><ScanLine size={18} /></button>
        </form>

        {loading && <Loader2 className="animate-spin text-[#2ec9c9] mt-6 mx-auto" size={28} />}

        {result && result.status === "ok" && (
          <div className="mt-6 bg-green-50 border border-green-100 rounded-2xl p-5 text-center" data-testid="checkin-success">
            <CheckCircle2 className="text-green-600 mx-auto mb-2" size={40} />
            <p className="text-green-700 font-bold text-lg">Welcome!</p>
            <p className="text-[#16243b] font-semibold mt-1 flex items-center justify-center gap-1"><User size={16} /> {result.attendee_name}</p>
            <p className="text-[#5a6b80] text-sm">{result.tier_name} · {result.product_title}</p>
            {result.event_date && <p className="text-xs text-[#8a97a8] mt-1">{fmtDate(result.event_date)}</p>}
          </div>
        )}
        {result && result.status === "pass" && (
          <div className="mt-6 bg-green-50 border border-green-200 rounded-2xl p-5 text-center" data-testid="checkin-pass">
            <BadgeCheck className="text-green-600 mx-auto mb-2" size={40} />
            <p className="text-green-700 font-bold text-lg">VALID · {result.pass_type}</p>
            <p className="text-[#16243b] font-semibold mt-1 flex items-center justify-center gap-1"><User size={16} /> {result.holder_name}</p>
            <p className="text-[#5a6b80] text-sm">Free access to all live events</p>
          </div>
        )}
        {result && result.status === "revoked" && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-2xl p-5 text-center" data-testid="checkin-pass-revoked">
            <Ban className="text-red-500 mx-auto mb-2" size={40} />
            <p className="text-red-600 font-bold text-lg">Pass revoked</p>
            <p className="text-[#16243b] font-semibold mt-1">{result.holder_name} · {result.pass_type}</p>
            <p className="text-[#5a6b80] text-sm mt-1">This pass is no longer valid. Do not admit.</p>
          </div>
        )}
        {result && result.status === "expired" && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-2xl p-5 text-center" data-testid="checkin-pass-expired">
            <Ban className="text-red-500 mx-auto mb-2" size={40} />
            <p className="text-red-600 font-bold text-lg">Pass expired</p>
            <p className="text-[#16243b] font-semibold mt-1">{result.holder_name} · {result.pass_type}</p>
            <p className="text-[#5a6b80] text-sm mt-1">This pass expired{result.expires_at ? ` on ${new Date(result.expires_at).toLocaleDateString()}` : ""}. Do not admit.</p>
          </div>
        )}
        {result && result.status === "already" && (
          <div className="mt-6 bg-amber-50 border border-amber-100 rounded-2xl p-5 text-center" data-testid="checkin-already">
            <AlertTriangle className="text-amber-600 mx-auto mb-2" size={40} />
            <p className="text-amber-700 font-bold text-lg">Already checked in</p>
            <p className="text-[#16243b] font-semibold mt-1">{result.attendee_name} · {result.tier_name}</p>
            {result.checked_in_at && <p className="text-xs text-[#8a97a8] mt-1">Admitted at {new Date(result.checked_in_at).toLocaleString("en-GB")}</p>}
          </div>
        )}
        {result && result.status === "error" && (
          <div className="mt-6 bg-red-50 border border-red-100 rounded-2xl p-5 text-center" data-testid="checkin-error">
            <XCircle className="text-red-500 mx-auto mb-2" size={40} />
            <p className="text-red-600 font-bold text-lg">Invalid ticket</p>
            <p className="text-[#5a6b80] text-sm mt-1">{result.detail}</p>
          </div>
        )}
      </div>
      {scan && <QRScanner onResult={(c) => { setScan(false); doCheckin(c); }} onClose={() => setScan(false)} />}
    </div>
  );
}
